import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * Everything that can be done about a post's picture, in one place.
 *
 * These were four buttons in the sheet's footer, and they carry the four longest labels in the
 * portal — "Upload your own photograph", "Draw another AI picture", "Use my photograph instead".
 * On a phone they wrapped the footer to four rows and pushed the previews, which are what the
 * sheet is for, into a strip. They are also the one group in that footer that plainly belongs
 * together: every one of them answers "what should this post show".
 *
 * Opened by a short button, closed by choosing or by Escape. Never the browser's own dialogs.
 */
export default function PictureDialog({ open, options, onClose }) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return undefined

    const onKey = (event) => {
      if (event.key !== 'Escape') return

      // Escape closes this and not the sheet underneath, which listens for the same key.
      event.stopPropagation()
      onClose()
    }

    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  if (!open) return null

  // Into the body, above whatever raised it — the same reason the confirmation does it.
  return createPortal(
    <div className="overlay overlay--ask" role="presentation" onClick={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label={t('posts.picture')}
        onClick={(event) => event.stopPropagation()}
      >
        <h3>{t('posts.picture')}</h3>

        <div className="dialog__choices">
          {options.map((option) =>
            // Uploading is a file input wearing a button, so it cannot be one.
            option.file ? (
              <label key={option.key} className="button button--quiet">
                {option.label}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  disabled={option.disabled}
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    event.target.value = ''
                    if (!file) return

                    onClose()
                    option.onPick(file)
                  }}
                />
              </label>
            ) : (
              <button
                key={option.key}
                type="button"
                className="button button--quiet"
                disabled={option.disabled}
                onClick={() => {
                  onClose()
                  option.onPick()
                }}
              >
                {option.label}
              </button>
            ),
          )}
        </div>

        <div className="dialog__actions">
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
